import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, Clock, UserCheck, ChevronRight } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { US_STATES } from "../../constants/company";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const today = () => new Date().toISOString().slice(0, 10);

export default function Appointments() {
  const { user } = useAuth();
  const isStaff = user?.role === "staff" || user?.role === "admin";
  const [appointments, setAppointments] = useState([]);
  const [team, setTeam] = useState([]);
  const [memberId, setMemberId] = useState("");
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState(null);
  const [slot, setSlot] = useState("");
  const [notes, setNotes] = useState("");
  const [booking, setBooking] = useState(false);
  const [reschedId, setReschedId] = useState(null);
  const [rDate, setRDate] = useState("");
  const [rSlots, setRSlots] = useState(null);
  const [rSlot, setRSlot] = useState("");
  const [savingResched, setSavingResched] = useState(false);
  const [stateFilter, setStateFilter] = useState("all");
  const [declineId, setDeclineId] = useState(null);
  const [declineReason, setDeclineReason] = useState("");
  const [declining, setDeclining] = useState(false);
  const loadAppointments = () => api.get("/appointments").then((r) => setAppointments(r.data)).catch(() => {});

  const decline = async (id) => {
    setDeclining(true);
    try {
      await api.patch(`/appointments/${id}`, { status: "declined", reason: declineReason });
      toast.success("Request declined — the client has been emailed");
      setDeclineId(null); setDeclineReason("");
      loadAppointments();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeclining(false);
    }
  };

  useEffect(() => {
    loadAppointments();
    api.get("/public/team").then((r) => setTeam(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setSlots(null);
    setSlot("");
    if (memberId && date) {
      api.get(`/appointments/availability?team_member_id=${memberId}&date=${date}`)
        .then((r) => setSlots(r.data))
        .catch(() => setSlots({ slots: [], closed: false }));
    }
  }, [memberId, date]);

  const book = async () => {
    setBooking(true);
    try {
      await api.post("/appointments", { team_member_id: memberId, date, time_slot: slot, notes });
      toast.success("Appointment request sent — you'll get an email once the team confirms");
      setMemberId(""); setDate(""); setSlot(""); setNotes(""); setSlots(null);
      loadAppointments();
    } catch (err) {
      toast.error(formatApiError(err, "Booking failed"));
    } finally {
      setBooking(false);
    }
  };

  const cancel = async (id) => {
    try {
      await api.patch(`/appointments/${id}`, { status: "cancelled" });
      toast.success("Appointment cancelled");
      loadAppointments();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const approve = async (id) => {
    try {
      await api.patch(`/appointments/${id}`, { status: "confirmed" });
      toast.success("Appointment approved — confirmation email sent to the client");
      loadAppointments();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const checkIn = async (id) => {
    try {
      await api.post(`/appointments/${id}/check-in`);
      toast.success("Checked in — the team has been notified and is getting ready for you");
      loadAppointments();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    setRSlot("");
    setRSlots(null);
    if (reschedId && rDate) {
      const appt = appointments.find((a) => a.appointment_id === reschedId);
      if (appt) {
        api.get(`/appointments/availability?team_member_id=${appt.team_member_id}&date=${rDate}`)
          .then((r) => setRSlots(r.data))
          .catch(() => setRSlots({ slots: [], closed: false }));
      }
    }
  }, [reschedId, rDate]);

  const saveReschedule = async (id) => {
    setSavingResched(true);
    try {
      await api.patch(`/appointments/${id}`, { date: rDate, time_slot: rSlot });
      toast.success("Appointment updated — the client is notified by email");
      setReschedId(null);
      setRDate("");
      setRSlot("");
      loadAppointments();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingResched(false);
    }
  };

  const complete = async (id) => {
    try {
      await api.patch(`/appointments/${id}`, { status: "completed" });
      toast.success("Marked completed");
      loadAppointments();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="appointments-page" className="space-y-8 animate-fade-up">
      <div>
        <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-medium">{isStaff ? "Schedule" : "Book a visit"}</p>
        <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tight mt-1">Appointments</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        <Card className="lg:col-span-2 h-fit" data-testid="booking-card">
          <CardHeader>
            <CardTitle className="font-heading text-xl">Book an Appointment</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">1</span> Team member</Label>
              <Select value={stateFilter} onValueChange={setStateFilter}>
                <SelectTrigger data-testid="booking-state-filter" className="mb-2"><SelectValue placeholder="Filter by state" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All locations</SelectItem>
                  {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="booking-member-grid">
                {team.filter((m) => stateFilter === "all" || m.state === stateFilter).map((m) => (
                  <button key={m.user_id} type="button" onClick={() => setMemberId(m.user_id)}
                    data-testid={`member-card-${m.user_id}`}
                    className={`flex items-center gap-3 text-left border rounded-md p-4 transition-colors duration-200 ${
                      memberId === m.user_id ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"}`}>
                    {m.photo_url ? (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${m.photo_url}`} alt={m.name}
                        className="w-12 h-12 rounded-md object-cover shrink-0" data-testid={`member-photo-${m.user_id}`} />
                    ) : (
                      <div className="w-12 h-12 rounded-md bg-primary/10 flex items-center justify-center font-heading text-lg font-semibold text-primary shrink-0">
                        {m.name?.charAt(0)?.toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{m.name}</p>
                      {m.title && <p className="text-xs text-muted-foreground truncate">{m.title}{m.state ? ` · ${m.state}` : ""}</p>}
                    </div>
                  </button>
                ))}
                {team.filter((m) => stateFilter === "all" || m.state === stateFilter).length === 0 && (
                  <p className="text-xs text-muted-foreground sm:col-span-2">No tax pros in this state yet.</p>
                )}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">2</span> Date</Label>
              <Input type="date" min={today()} value={date} onChange={(e) => setDate(e.target.value)} data-testid="booking-date-input" disabled={!memberId} />
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">3</span> Time slot</Label>
              {!slots && <p className="text-xs text-muted-foreground" data-testid="slots-placeholder">Select a team member and date to see available times.</p>}
              {slots?.closed && <p className="text-xs text-destructive" data-testid="slots-closed-message">The office is closed on weekends. Please pick a weekday.</p>}
              {slots && !slots.closed && (
                <div className="grid grid-cols-4 gap-2" data-testid="time-slots-grid">
                  {slots.slots.map((s) => (
                    <button key={s.time} type="button" disabled={!s.available}
                      onClick={() => setSlot(s.time)}
                      data-testid={`slot-${s.time.replace(":", "")}`}
                      className={`text-xs py-2 rounded-md border transition-colors duration-200 ${
                        slot === s.time
                          ? "bg-primary text-primary-foreground border-primary"
                          : s.available
                            ? "border-border hover:bg-secondary/40"
                            : "border-border text-muted-foreground/40 line-through cursor-not-allowed"
                      }`}>
                      {s.time}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Notes (optional)</Label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What would you like to discuss?" data-testid="booking-notes-input" />
            </div>
            <Button className="w-full" disabled={!memberId || !date || !slot || booking} onClick={book} data-testid="book-appointment-button">
              {booking ? "Booking…" : "Confirm Booking"} <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardContent>
        </Card>

        <Card className="lg:col-span-3" data-testid="appointments-list-card">
          <CardHeader>
            <CardTitle className="font-heading text-xl">{isStaff ? "All Appointments" : "Your Appointments"}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {appointments.length === 0 && <p className="text-sm text-muted-foreground" data-testid="no-appointments-message">No appointments yet.</p>}
            {appointments.map((a) => (
              <div key={a.appointment_id} className="border border-border rounded-md p-4 flex flex-wrap items-center gap-4" data-testid={`appointment-card-${a.appointment_id}`}>
                <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                  <CalendarDays className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1 min-w-[160px]">
                  <p className="text-sm font-medium flex items-center gap-1.5">
                    <UserCheck className="w-3.5 h-3.5 text-muted-foreground" />
                    {isStaff ? `${a.client_name} with ${a.team_member_name}` : a.team_member_name}
                  </p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                    <Clock className="w-3 h-3" /> {a.date} at {a.time_slot}
                  </p>
                  {a.notes && <p className="text-xs text-muted-foreground mt-1 italic">“{a.notes}”</p>}
                </div>
                <Badge variant={["cancelled", "declined"].includes(a.status) ? "destructive" : a.status === "completed" ? "secondary" : a.status === "pending" ? "outline" : "default"} className="capitalize" data-testid={`appt-status-${a.appointment_id}`}>
                  {a.status}
                </Badge>
                {a.status === "declined" && a.decline_reason && (
                  <p className="w-full text-xs text-muted-foreground italic" data-testid={`appt-decline-reason-${a.appointment_id}`}>Team note: “{a.decline_reason}”</p>
                )}
                {a.checked_in_at && (
                  <Badge variant="secondary" data-testid={`appt-checked-in-${a.appointment_id}`}>Checked In</Badge>
                )}
                {!isStaff && a.status === "confirmed" && a.date === today() && !a.checked_in_at && (
                  <Button size="sm" onClick={() => checkIn(a.appointment_id)} data-testid={`appt-checkin-${a.appointment_id}`}>
                    Check In
                  </Button>
                )}
                {["pending", "confirmed"].includes(a.status) && (
                  <div className="flex flex-wrap gap-2">
                    {isStaff && a.status === "pending" && (
                      <>
                        <Button size="sm" onClick={() => approve(a.appointment_id)} data-testid={`appt-approve-${a.appointment_id}`}>Approve</Button>
                        <Button size="sm" variant="outline" className="text-destructive border-destructive/40"
                          onClick={() => { setDeclineId(declineId === a.appointment_id ? null : a.appointment_id); setDeclineReason(""); }}
                          data-testid={`appt-decline-${a.appointment_id}`}>Decline</Button>
                      </>
                    )}
                    {!isStaff && a.status === "pending" && (
                      <span className="text-xs text-muted-foreground self-center" data-testid={`appt-awaiting-${a.appointment_id}`}>Awaiting approval from your tax pro</span>
                    )}
                    <Button size="sm" variant="outline"
                      onClick={() => { setReschedId(reschedId === a.appointment_id ? null : a.appointment_id); setRDate(""); setRSlot(""); }}
                      data-testid={`appt-reschedule-${a.appointment_id}`}>Reschedule</Button>
                    {isStaff && a.status === "confirmed" && (
                      <Button size="sm" variant="outline" onClick={() => complete(a.appointment_id)} data-testid={`appt-complete-${a.appointment_id}`}>Complete</Button>
                    )}
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => cancel(a.appointment_id)} data-testid={`appt-cancel-${a.appointment_id}`}>Cancel</Button>
                  </div>
                )}
                {declineId === a.appointment_id && (
                  <div className="w-full border-t border-border pt-4 mt-2 space-y-3" data-testid={`decline-panel-${a.appointment_id}`}>
                    <div className="space-y-1.5 max-w-md">
                      <Label>Reason for declining (optional — the client will see this in their email)</Label>
                      <Textarea rows={2} value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} maxLength={500}
                        placeholder="e.g. I'm out of the office that day — please pick a time next week." data-testid="decline-reason-input" />
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="destructive" disabled={declining} onClick={() => decline(a.appointment_id)} data-testid="decline-confirm-button">
                        {declining ? "Sending…" : "Decline & Notify Client"}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setDeclineId(null)} data-testid="decline-cancel-button">Close</Button>
                    </div>
                  </div>
                )}
                {reschedId === a.appointment_id && (
                  <div className="w-full border-t border-border pt-4 mt-2 space-y-3" data-testid={`reschedule-panel-${a.appointment_id}`}>
                    <div className="space-y-1.5 max-w-xs">
                      <Label>New date</Label>
                      <Input type="date" min={today()} value={rDate} onChange={(e) => setRDate(e.target.value)} data-testid="reschedule-date-input" />
                    </div>
                    {rSlots?.closed && <p className="text-xs text-destructive" data-testid="reschedule-closed-message">Closed on this day — pick another date.</p>}
                    {rSlots && !rSlots.closed && (
                      <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="reschedule-slots-grid">
                        {rSlots.slots.map((s) => (
                          <button key={s.time} type="button" disabled={!s.available} onClick={() => setRSlot(s.time)}
                            data-testid={`reschedule-slot-${s.time.replace(":", "")}`}
                            className={`text-xs py-2 rounded-md border transition-colors duration-200 ${
                              rSlot === s.time
                                ? "bg-primary text-primary-foreground border-primary"
                                : s.available
                                  ? "border-border hover:bg-secondary/40"
                                  : "border-border text-muted-foreground/40 line-through cursor-not-allowed"
                            }`}>
                            {s.time}
                          </button>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Button size="sm" disabled={!rDate || !rSlot || savingResched} onClick={() => saveReschedule(a.appointment_id)} data-testid="reschedule-save-button">
                        {savingResched ? "Saving…" : "Save New Time"}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setReschedId(null)} data-testid="reschedule-cancel-button">Close</Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
